/**
 * DI Graph Serializer
 *
 * Deterministic serialization for design.json: every collection sorted, no
 * timestamps, workspace-relative posix paths only — so the committed file is
 * byte-identical across regenerations of an unchanged graph.
 */

import { DiDesign, DiEdge, DiGraph, DiNode } from './model';

function compareNodes(a: DiNode, b: DiNode): number {
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

function edgeKey(edge: DiEdge): string {
    return `${edge.from} ${edge.to} ${edge.tokenKey} ${edge.paramName}`;
}

function compareEdges(a: DiEdge, b: DiEdge): number {
    const keyA = edgeKey(a);
    const keyB = edgeKey(b);
    return keyA < keyB ? -1 : keyA > keyB ? 1 : 0;
}

/** Sort one design's collections in place. */
function sortDesign(design: DiDesign): void {
    design.nodes.sort(compareNodes);
    design.edges.sort(compareEdges);
    design.unresolved = [...new Set(design.unresolved)].sort();
}

/** Sort all graph collections in place (stable output order). */
export function sortGraph(graph: DiGraph): DiGraph {
    graph.designs.sort((a: DiDesign, b: DiDesign) => (a.root < b.root ? -1 : a.root > b.root ? 1 : 0));
    for (const design of graph.designs) {
        sortDesign(design);
    }
    return graph;
}

/** Serialize a (sorted) graph to the design.json file content. */
export function toDesignJson(graph: DiGraph): string {
    sortGraph(graph);
    return JSON.stringify(graph, null, 4) + '\n';
}
